import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Navbar from "../../components/Navbar/Navbar";
import "./GChatBuckets.css";

const PRESETS = [
  { label: "Last 1 Week", days: 7 },
  { label: "Last 2 Weeks", days: 14 },
  { label: "Last 1 Month", days: 30 },
  { label: "Last 3 Months", days: 90 },
];

const toInputDate = (d) => d.toISOString().split("T")[0];

const formatMonth = (monthStr) => {
  if (!monthStr) return "";
  const [y, m] = monthStr.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
};

const GChatBuckets = () => {
  const today = toInputDate(new Date());
  const [startDate, setStartDate] = useState(toInputDate(new Date(Date.now() - 30 * 86400000)));
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [jobs, setJobs] = useState([]);
  const [showJobLogs, setShowJobLogs] = useState(null);

  const [runs, setRuns] = useState([]);
  const [selectedJobKey, setSelectedJobKey] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [buckets, setBuckets] = useState([]);
  const [expandedBucket, setExpandedBucket] = useState(null);
  const [loadingBuckets, setLoadingBuckets] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get("/gchat/bucketise/jobs");
      setJobs(res.data);
    } catch (err) {
      console.error("Failed to fetch chat bucket jobs:", err);
    }
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      const res = await axios.get("/gchat/buckets/runs");
      setRuns(res.data);
    } catch (err) {
      console.error("Failed to fetch chat bucket runs:", err);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    fetchRuns();
  }, [fetchJobs, fetchRuns]);

  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === "running" || j.status === "pending");
    if (!hasActive) return;
    const interval = setInterval(() => {
      fetchJobs();
      fetchRuns();
    }, 3000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs, fetchRuns]);

  const groupedJobs = useMemo(() => {
    const jobMap = {};
    runs.forEach((r) => {
      const key = r.job_id || r.id;
      if (!jobMap[key]) {
        jobMap[key] = {
          key,
          isLegacy: !r.job_id,
          runs: [],
          totalThreads: 0,
          totalBuckets: 0,
          startDate: r.start_date,
          endDate: r.end_date,
          createdAt: r.created_at,
        };
      }
      jobMap[key].runs.push(r);
      jobMap[key].totalThreads += r.total_threads || 0;
      jobMap[key].totalBuckets += r.total_buckets || 0;
      if (r.start_date < jobMap[key].startDate) jobMap[key].startDate = r.start_date;
      if (r.end_date > jobMap[key].endDate) jobMap[key].endDate = r.end_date;
    });
    Object.values(jobMap).forEach((g) => {
      g.runs.sort((a, b) => (a.period_month || "").localeCompare(b.period_month || ""));
    });
    return Object.values(jobMap).sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
  }, [runs]);

  const selectedGroup = useMemo(
    () => groupedJobs.find((g) => g.key === selectedJobKey) || null,
    [groupedJobs, selectedJobKey]
  );

  const monthRuns = useMemo(
    () => (selectedGroup ? selectedGroup.runs : []),
    [selectedGroup]
  );

  const handlePreset = (days) => {
    setStartDate(toInputDate(new Date(Date.now() - days * 86400000)));
    setEndDate(toInputDate(new Date()));
  };

  const handleRun = async () => {
    setError("");
    setSuccess("");
    if (!startDate || !endDate) { setError("Select both dates."); return; }
    if (startDate > endDate) { setError("Start must be before end."); return; }
    setLoading(true);
    try {
      const res = await axios.post("/gchat/bucketise/run", { start_date: startDate, end_date: endDate });
      setSuccess(`Chat bucketisation job ${res.data.job_id} started.`);
      await fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to start job.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectJob = (key) => {
    if (!key) {
      setSelectedJobKey(null);
      setSelectedMonth(null);
      setBuckets([]);
      return;
    }
    setSelectedJobKey(key);
    setSelectedMonth(null);
    setBuckets([]);
    setExpandedBucket(null);

    const group = groupedJobs.find((g) => g.key === key);
    if (group && group.runs.length === 1) {
      loadBucketsForRun(group.runs[0].id);
      setSelectedMonth(group.runs[0].period_month || "all");
    }
  };

  const handleDeleteJob = async () => {
    if (!selectedJobKey) return;
    if (!window.confirm("Delete all bucketisation results for this job? This cannot be undone.")) return;
    try {
      await axios.delete(`/gchat/buckets/jobs/${selectedJobKey}`);
      setSelectedJobKey(null);
      setSelectedMonth(null);
      setBuckets([]);
      setExpandedBucket(null);
      await fetchRuns();
      await fetchJobs();
    } catch (err) {
      console.error("Failed to delete job:", err);
    }
  };

  const handleSelectMonth = (run) => {
    setSelectedMonth(run.period_month || "all");
    loadBucketsForRun(run.id);
  };

  const loadBucketsForRun = async (runId) => {
    setLoadingBuckets(true);
    setExpandedBucket(null);
    try {
      const res = await axios.get(`/gchat/buckets/runs/${runId}`);
      setBuckets(res.data);
    } catch (err) {
      console.error("Failed to load chat buckets:", err);
    } finally {
      setLoadingBuckets(false);
    }
  };

  const isRunning = jobs.some((j) => j.status === "running" || j.status === "pending");

  return (
    <div>
      <Navbar />
      <div className="gchat-buckets-container">
        <div className="gchat-buckets-header">
          <h1>Chat Root Cause Bucketisation</h1>
          <p>Cluster Google Chat conversation root causes into meaningful categories using AI.</p>
        </div>

        <div className="gchat-buckets-card">
          <h2>Run Bucketisation</h2>
          <div className="gchat-date-form">
            <div className="gchat-date-field">
              <label>Start Date</label>
              <input type="date" value={startDate} max={today} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="gchat-date-field">
              <label>End Date</label>
              <input type="date" value={endDate} max={today} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <button className="gchat-run-btn" onClick={handleRun} disabled={loading || isRunning}>
              {loading ? "Starting..." : isRunning ? "Running..." : "Run Bucketisation"}
            </button>
          </div>
          <div className="gchat-presets">
            {PRESETS.map((p) => (
              <button key={p.days} className="gchat-preset-btn" onClick={() => handlePreset(p.days)}>{p.label}</button>
            ))}
          </div>
          {error && <p className="gchat-error-msg">{error}</p>}
          {success && <p className="gchat-success-msg">{success}</p>}

          {jobs.length > 0 && (
            <div style={{ marginTop: 20 }}>
              {jobs.slice(0, 3).map((job) => (
                <div key={job.id}>
                  <div className="gchat-job-status-row">
                    <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{job.id}</span>
                    <span>{job.start_date} &rarr; {job.end_date}</span>
                    <span className={`gchat-status-badge gchat-status-${job.status}`}>{job.status}</span>
                    <span style={{ color: "#718096" }}>
                      {job.total_threads} conversations &rarr; {job.total_buckets} buckets
                      {job.months_total > 0 && ` (${job.months_processed || 0}/${job.months_total} months)`}
                    </span>
                    <button className="gchat-log-toggle-btn" onClick={() => setShowJobLogs(showJobLogs === job.id ? null : job.id)}>
                      {showJobLogs === job.id ? "Hide" : "Logs"}
                    </button>
                  </div>
                  {showJobLogs === job.id && (
                    <div className="gchat-log-panel-small">
                      {job.logs && job.logs.length > 0
                        ? job.logs.map((l, i) => <p key={i}>{l}</p>)
                        : <p>No logs yet.</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="gchat-buckets-card">
          <h2>View Buckets</h2>
          {groupedJobs.length === 0 ? (
            <p className="gchat-empty-state">No chat bucketisation runs yet. Run one above to get started.</p>
          ) : (
            <>
              <div className="gchat-run-selector">
                <select value={selectedJobKey || ""} onChange={(e) => handleSelectJob(e.target.value)}>
                  <option value="">Select a job...</option>
                  {groupedJobs.map((g) => (
                    <option key={g.key} value={g.key}>
                      {g.startDate} &rarr; {g.endDate} &mdash; {g.totalThreads} conversations, {g.totalBuckets} buckets
                      {g.runs.length > 1 ? ` (${g.runs.length} months)` : ""}
                    </option>
                  ))}
                </select>
                {selectedJobKey && (
                  <>
                    <button
                      className="gchat-csv-download-btn"
                      onClick={async () => {
                        try {
                          const res = await axios.get(`/gchat/buckets/jobs/${selectedJobKey}/csv`, { responseType: "blob" });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `chat_buckets_${selectedJobKey}.csv`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch (err) {
                          alert("Failed to download CSV.");
                        }
                      }}
                      title="Download all buckets for this job as CSV"
                    >
                      Download CSV
                    </button>
                    <button className="gchat-delete-job-btn" onClick={handleDeleteJob} title="Delete this job's results">
                      Delete Job
                    </button>
                  </>
                )}
              </div>

              {selectedGroup && monthRuns.length > 1 && (
                <div className="gchat-month-tabs">
                  {monthRuns.map((r) => (
                    <button
                      key={r.id}
                      className={`gchat-month-tab ${selectedMonth === (r.period_month || "all") ? "gchat-month-tab-active" : ""}`}
                      onClick={() => handleSelectMonth(r)}
                    >
                      {r.period_month ? formatMonth(r.period_month) : "All"}
                      <span className="gchat-month-tab-count">{r.total_threads}</span>
                    </button>
                  ))}
                </div>
              )}

              {loadingBuckets && <p style={{ color: "#718096" }}>Loading buckets...</p>}

              {!loadingBuckets && selectedJobKey && buckets.length > 0 && (
                <div className="gchat-bucket-grid">
                  {buckets.map((bucket) => (
                    <div key={bucket.id} className={`gchat-bucket-card ${bucket.name === "Miscellaneous" ? "gchat-bucket-card-misc" : ""}`}>
                      <div
                        className="gchat-bucket-card-header"
                        onClick={() => setExpandedBucket(expandedBucket === bucket.id ? null : bucket.id)}
                      >
                        <h3>{bucket.name}</h3>
                        <span className="gchat-bucket-count">{bucket.thread_count} conversations</span>
                      </div>
                      <div className="gchat-bucket-description">{bucket.description}</div>
                      {expandedBucket === bucket.id && (
                        <div className="gchat-bucket-threads">
                          {bucket.threads.map((t) => (
                            <div key={t.id} className="gchat-bucket-thread-row">
                              {t.thread_link ? (
                                <a
                                  className="gchat-thread-link"
                                  href={t.thread_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Open in Google Chat"
                                >
                                  View Thread
                                </a>
                              ) : (
                                <span className="gchat-thread-link">#{t.id}</span>
                              )}
                              <div className="gchat-thread-info">
                                <div className="gchat-thread-issue">{t.issue_description}</div>
                                <div className="gchat-thread-meta">
                                  <span className="gchat-space-badge">{t.space_display_name}</span>
                                  {t.conversation_start && <span>{new Date(t.conversation_start).toLocaleDateString()}</span>}
                                </div>
                                {t.root_cause_summary && (
                                  <div className="gchat-thread-root-cause">{t.root_cause_summary}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {!loadingBuckets && selectedJobKey && selectedMonth && buckets.length === 0 && (
                <p className="gchat-empty-state">No buckets found for this run.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GChatBuckets;
